import React, { useEffect, useState } from "react";
import { Plus, Trash2, X, Download, Scale, Search, Edit, Info, FileText, Upload, FileDown, FilePlus, EyeOff, Bell, MailOpen, Filter, Columns3, ChevronRight, ChevronDown, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, AYLAR, formatTL, formatNum, API, getToken, openPdf } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import SignatureModal from "@/components/SignatureModal";
import PrimModal from "@/components/prim/PrimModal";
import ImportModal from "@/components/prim/ImportModal";
import PrimTrashModal from "@/components/PrimTrashModal";
import VarlikAuditModal from "@/components/prim/VarlikAuditModal";
import TagManagerModal from "@/components/prim/TagManagerModal";
import PrimMobileCards from "@/components/prim/PrimMobileCards";
import PrimTable from "@/components/prim/PrimTable";
import PrimExportModal from "@/components/prim/PrimExportModal";
import SavedFilterPresets from "@/components/SavedFilterPresets";
import QuickDateFilters from "@/components/QuickDateFilters";
import { DateInput } from "@/components/DateInput";
import { ShiftedMonthToggle, isMonthShifted } from "@/components/prim/PrimMonth";
import { PrimStatementModal } from "@/components/prim/PrimStatementModal";

// Universal column filter catalogue for /prim. Mirrors backend PRIM_FILTERABLE_KEYS.
const PRIM_COLUMN_META = [
  { k: "tarih",              l: "Tarih",                      t: "date" },
  { k: "alacakli_adi",       l: "Müvekkil (Alacaklı)",        t: "text" },
  { k: "icra_foy",           l: "İcra Föy",                   t: "text" },
  { k: "icra_esas",          l: "İcra Esas",                  t: "text" },
  { k: "dosya_no",           l: "Dosya No",                   t: "text" },
  { k: "tahsilat_miktari",   l: "Tahsilat Miktarı (₺)",       t: "number" },
  { k: "vekalet_ucreti",     l: "Vekalet Ücreti (₺)",         t: "number" },
  { k: "oran",               l: "Oran (%)",                   t: "number" },
  { k: "prim_payi",          l: "Prim Payı (₺)",              t: "number" },
  { k: "odenecek_toplam",    l: "Ödenecek Toplam (₺)",        t: "number" },
  { k: "odenecek_personel",  l: "Ödenecek Personel (₺)",      t: "number" },
  { k: "odenecek_arastirici",l: "Ödenecek Araştırıcı (₺)",    t: "number" },
  { k: "arastiran_isim",     l: "Araştıran",                  t: "text" },
];
const PRIM_COLUMN_META_BY_KEY = Object.fromEntries(PRIM_COLUMN_META.map((c) => [c.k, c]));

function serializePrimColFilters(colFilters) {
  const out = {};
  for (const [k, c] of Object.entries(colFilters || {})) {
    if (!c) continue;
    const meta = PRIM_COLUMN_META_BY_KEY[k];
    if (!meta) continue;
    if (meta.t === "text") {
      if ((c.value || "").trim()) out[k] = { value: c.value.trim() };
    } else {
      const o = {};
      if (c.min !== undefined && c.min !== "") o.min = c.min;
      if (c.max !== undefined && c.max !== "") o.max = c.max;
      if (Object.keys(o).length) out[k] = o;
    }
  }
  return Object.keys(out).length ? JSON.stringify(out) : "";
}

function countPrimColFilters(colFilters) {
  return Object.values(colFilters || {}).filter((c) => {
    if (!c) return false;
    if ((c.value || "").toString().trim()) return true;
    if (c.min !== undefined && c.min !== "") return true;
    if (c.max !== undefined && c.max !== "") return true;
    return false;
  }).length;
}

export default function PrimTab({ yil, ay }) {
  const { canSeePrimRate, canSeeMoney, isPersonel, isAdmin, isMuhasebeci } = useAuth();
  const [rows, setRows] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [statementOpen, setStatementOpen] = useState(false);
  const [stAccess, setStAccess] = useState(null);
  useEffect(() => {
    if (isPersonel) api.get("/prim-statement/access").then((r) => setStAccess(r.data)).catch(() => setStAccess(null));
  }, [isPersonel]);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filterAll, setFilterAll] = useState(false);
  const [personelFilter, setPersonelFilter] = useState("");
  const [onlyCC, setOnlyCC] = useState(false);
  const [onlyShifted, setOnlyShifted] = useState(false);
  const [signaturePrim, setSignaturePrim] = useState(null);
  const [sigLoading, setSigLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [inboxMessages, setInboxMessages] = useState([]);
  const [inboxLoading, setInboxLoading] = useState(false);
  // Universal column filters (admin/muhasebeci only).
  const [colFilters, setColFilters] = useState({});
  const [addColKey, setAddColKey] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [varlikAuditOpen, setVarlikAuditOpen] = useState(false);
  const [tagMgrOpen, setTagMgrOpen] = useState(false);
  // Multi-select tag filter: when non-empty, only rows containing the tag(s) are shown.
  // `tagFilterMode` toggles between OR (any) and AND (all) across selected chips.
  const [tagFilter, setTagFilter] = useState([]); // array of tag strings (preserves user casing)
  const [tagFilterMode, setTagFilterMode] = useState("or"); // "or" | "and"
  // Approval filter: null = all, true = onaylanan, false = onay bekleyen.
  const [approvalFilter, setApprovalFilter] = useState(null);

  // Deep-link: if session carries primJumpCC flag, auto-activate CC filter once
  useEffect(() => {
    try {
      if (typeof window !== "undefined" && window.sessionStorage.getItem("primJumpCC") === "1") {
        setOnlyCC(true);
        window.sessionStorage.removeItem("primJumpCC");
      }
    } catch { /* ignore storage denied */ }
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (!filterAll) {
        params.set("yil", String(yil));
        params.set("ay", String(ay));
      }
      const cfStr = serializePrimColFilters(colFilters);
      if (cfStr) params.set("cf", cfStr);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const [r, p, pg] = await Promise.all([
        api.get(`/prim${qs}`),
        api.get("/personnel"),
        // Also fetch personnel in the Giden scope (Haciz Birimi + Call Center by
        // default). This is required for the personel role whose /personnel
        // returns only their own record; without it the Prim modal's giden
        // dropdown stays stuck on "Personel yükleniyor".
        api.get("/personnel/scope/giden").catch(() => ({ data: { rows: [] } })),
      ]);
      setRows(r.data.rows);
      // Merge full personnel list with giden-scope rows, de-duplicated by id so
      // the modal's gidenSelectablePersonnel filter can still find them.
      const byId = new Map();
      (p.data || []).forEach((row) => byId.set(row.id, row));
      (pg.data?.rows || []).forEach((row) => { if (!byId.has(row.id)) byId.set(row.id, row); });
      setPersonnel(Array.from(byId.values()));
    } catch {
      toast.error("Prim verileri yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [yil, ay, filterAll]);

  // Personel inbox (admin reject reasons)
  const loadInbox = async () => {
    if (!isPersonel) return;
    setInboxLoading(true);
    try {
      const r = await api.get("/me/messages");
      setInboxMessages(r.data?.rows || []);
    } catch {
      setInboxMessages([]);
    } finally {
      setInboxLoading(false);
    }
  };
  useEffect(() => { loadInbox(); /* eslint-disable-next-line */ }, [isPersonel]);

  const dismissInboxMessage = async (id) => {
    try {
      await api.post(`/me/messages/${id}/read`);
      setInboxMessages((msgs) => msgs.map((m) => m.id === id ? { ...m, read_at: new Date().toISOString() } : m));
    } catch { /* ignore */ }
  };
  const dismissAllInbox = async () => {
    try {
      await api.post("/me/messages/read-all");
      setInboxMessages((msgs) => msgs.map((m) => ({ ...m, read_at: m.read_at || new Date().toISOString() })));
      toast.success("Tüm bildirimler okundu olarak işaretlendi");
    } catch (e) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu prim kaydı silinsin mi?")) return;
    await api.delete(`/prim/${id}`);
    toast.success("Prim silindi");
    load();
  };

  const handleApprove = async (prim) => {
    // Multi-pozisyon uyarısı: Giden'de farklı pozisyonlar varsa onaylamadan önce bilgilendir
    try {
      const pozs = Array.from(new Set((prim.giden_pozisyonlari || [])
        .map((p) => (p || "").trim())
        .filter(Boolean)));
      if (pozs.length >= 2) {
        const hasCC = pozs.some((p) => /call ?center|çağrı|cagri/i.test(p));
        const header = hasCC
          ? "Dikkat: Bu prim satırı KARIŞIK POZİSYON içeriyor (Call Center dahil)."
          : "Dikkat: Bu prim satırı KARIŞIK POZİSYON içeriyor.";
        const detail = pozs.map((p) => `• ${p}`).join("\n");
        const ok = window.confirm(
          `${header}\n\nGiden pozisyonları:\n${detail}\n\nHer pozisyon farklı oran kuralı ile hesaplanmış olabilir. Onaylıyor musunuz?`
        );
        if (!ok) return;
      }
    } catch { /* best-effort warning only */ }
    try {
      await api.post(`/prim/${prim.id}/approve`);
      toast.success("Prim onaylandı · bordroya yansıyacak");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Onay başarısız");
    }
  };

  const handleReject = async (prim) => {
    if (!window.confirm(`"${prim.alacakli_adi || prim.dosya_adi}" primi reddedilsin mi? Bu işlemle kayıt silinir ve geri alınamaz.`)) return;
    try {
      await api.post(`/prim/${prim.id}/reject`);
      toast.success("Prim reddedildi · kayıt silindi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reddetme başarısız");
    }
  };

  const handleToggleVarlik = async (prim) => {
    try {
      const r = await api.patch(`/prim/${prim.id}/varlik-toggle`);
      toast.success(`Varlık şirketi: ${r.data?.varlik_sirketi ? "EVET" : "HAYIR"} · oto-oran %${r.data?.oran}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Varlık değiştirilemedi");
    }
  };

  const handleSetVisibility = async (prim, visibility) => {
    try {
      await api.patch(`/prim/${prim.id}/personel-visibility`, { visibility });
      const msg = visibility === "forced_visible" ? "Personel kalıcı görecek"
                : visibility === "forced_hidden"  ? "Personel'den gizlendi"
                                                   : "Varsayılan 7 gün kuralına döndü";
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Görünürlük değiştirilemedi");
    }
  };

  const toggleRowSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearRowSelection = () => setSelectedIds(new Set());

  const bulkSetVarlik = async (newVarlik) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`${ids.length} prim için varlık şirketi = ${newVarlik ? "EVET" : "HAYIR"} olarak ayarlansın mı?\nOto-oran yeniden hesaplanacak.`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/prim/bulk-varlik", { ids, varlik_sirketi: newVarlik });
      toast.success(`${r.data?.updated || 0} prim güncellendi · ${r.data?.skipped || 0} zaten uygundu`);
      clearRowSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toplu güncelleme başarısız");
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const first = window.confirm(
      `⚠️ ${ids.length} prim kaydı KALICI OLARAK SİLİNECEK.\n\nSilinen kayıtlar:\n• Prim listesinden kaldırılır\n• Bordroya daha sonra yansımaz (onaylıysa önceki hesaplar değişebilir)\n• Prim onay/ret geçmişi de silinir\n\nDevam etmek istiyor musunuz?`
    );
    if (!first) return;
    const confirm2 = window.prompt(`Güvenlik için SİL yazarak onaylayın (${ids.length} kayıt):`);
    // Normalize Turkish variants (sil / Sil / SIL / SİL) → compare to "SIL"
    const normalized = (confirm2 || "").trim().replace(/i/g, "I").replace(/İ/g, "I").toUpperCase();
    if (normalized !== "SIL") {
      toast.info("İşlem iptal edildi");
      return;
    }
    setBulkBusy(true);
    try {
      const r = await api.post("/prim/bulk-delete", { ids });
      toast.success(`${r.data?.deleted || 0} prim çöp kutusuna taşındı · 24 saat içinde geri yükleyebilirsiniz${r.data?.not_found ? ` · ${r.data.not_found} bulunamadı` : ""}`, { duration: 6000 });
      clearRowSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toplu silme başarısız");
    } finally {
      setBulkBusy(false);
    }
  };

  const handlePdf = (prim) => {
    // Open signature modal first; actual download happens after submit/skip
    setSignaturePrim(prim);
  };

  const handleSignatureSubmit = async ({ imza_base64, save_to_prim }) => {
    if (!signaturePrim) return;
    setSigLoading(true);
    try {
      const res = await api.post(`/prim/${signaturePrim.id}/pdf`, { imza_base64, save_to_prim }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `prim_makbuz_${signaturePrim.id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success("PDF makbuz indirildi");
      setSignaturePrim(null);
      if (save_to_prim) load();
    } catch (e) {
      toast.error("PDF oluşturulamadı");
    } finally {
      setSigLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get("/prim/import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = "prim_import_sablon.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Şablon indirilemedi");
    }
  };

  const handleAllPdf = () => {
    if (filterAll) {
      toast.error("Toplu PDF için belirli bir ay seçin");
      return;
    }
    openPdf(`/prim/all-pdf?yil=${yil}&ay=${ay}`);
  };

  // Note: Prim export flow lives in PrimExportModal (opened via `showExportModal`).
  // Legacy `handleExport` helper removed in favour of the richer modal.

  const filtered = rows.filter((r) => {
    const matchesQuery = [r.dosya_adi, r.alacakli_adi, r.icra_foy, r.icra_esas]
      .some((f) => (f || "").toLowerCase().includes(query.toLowerCase()));
    if (!matchesQuery) return false;
    if (personelFilter) {
      const inGiden = (r.giden_kisiler || []).includes(personelFilter);
      const isArastiran = r.arastiran_kisi_id === personelFilter;
      if (!inGiden && !isArastiran) return false;
    }
    if (onlyShifted && !isMonthShifted(r)) return false;
    if (onlyCC) {
      // Row has CC if any giden pozisyon matches CC OR backend tagged has_call_center
      const hasCCFromPoz = (r.giden_pozisyonlari || []).some((p) => /call ?center|çağrı|cagri/i.test(p || ""));
      if (!(r.has_call_center || hasCCFromPoz)) return false;
    }
    if (tagFilter.length) {
      const rowTags = new Set((r.tags || []).map((t) => (t || "").toLocaleLowerCase("tr")));
      if (tagFilterMode === "and") {
        if (!tagFilter.every((t) => rowTags.has(t.toLocaleLowerCase("tr")))) return false;
      } else {
        if (!tagFilter.some((t) => rowTags.has(t.toLocaleLowerCase("tr")))) return false;
      }
    }
    if (approvalFilter !== null) {
      const isApproved = r.onaylandi === true;
      if (approvalFilter && !isApproved) return false;
      if (!approvalFilter && isApproved) return false;
    }
    return true;
  });

  const approvedCount = rows.filter((r) => r.onaylandi === true).length;
  const pendingCount = rows.length - approvedCount;

  // Build unique tag list from current rows (case-insensitive, preserve first-seen casing)
  const tagChipCounts = (() => {
    const map = new Map();
    for (const r of rows) {
      for (const t of (r.tags || [])) {
        if (!t) continue;
        const key = t.toLocaleLowerCase("tr");
        const cur = map.get(key) || { label: t, count: 0 };
        cur.count += 1;
        map.set(key, cur);
      }
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, "tr"));
  })();

  // Per-personnel breakdown for the selected personel (giden share + araştıran share)
  const personelDetay = personelFilter ? filtered.reduce((acc, r) => {
    const inGiden = (r.giden_kisiler || []).includes(personelFilter);
    const isArastiran = r.arastiran_kisi_id === personelFilter;
    if (inGiden) {
      acc.giden_adet += 1;
      acc.giden_prim += Number(r.kisi_basi_prim) || 0;
    }
    if (isArastiran) {
      acc.arastiran_adet += 1;
      acc.arastiran_prim += Number(r.arastirma_primi) || 0;
    }
    return acc;
  }, { giden_adet: 0, giden_prim: 0, arastiran_adet: 0, arastiran_prim: 0 }) : null;

  const totals = filtered.reduce((acc, r) => {
    acc.vekalet += Number(r.vekalet_ucreti) || 0;
    acc.tahsilat += Number(r.tahsilat_miktari) || 0;
    acc.odenecek += Number(r.odenecek_toplam) || 0;
    acc.kisi_basi += Number(r.kisi_basi_prim) || 0;
    acc.arastirma_primi += Number(r.arastirma_primi) || 0;
    // CC total: sum per-person amount for every CC personel in gidens
    const detay = r.kisi_basi_detay || {};
    const gids = r.giden_kisiler || [];
    const pozs = r.giden_pozisyonlari || [];
    gids.forEach((gid, i) => {
      if (/call ?center|çağrı|cagri/i.test(pozs[i] || "")) {
        acc.cc += Number(detay[gid]) || 0;
      }
    });
    return acc;
  }, { vekalet: 0, tahsilat: 0, odenecek: 0, kisi_basi: 0, arastirma_primi: 0, cc: 0 });

  return (
    <div className="space-y-4">
      {isPersonel && inboxMessages.length > 0 && (
        <PersonelInboxBanner
          messages={inboxMessages}
          onDismiss={dismissInboxMessage}
          onDismissAll={dismissAllInbox}
        />
      )}
      {isPersonel && stAccess?.acik && (
        <div className="flex justify-end">
          <button type="button" onClick={() => setStatementOpen(true)} data-testid="prim-statement-open"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-sm transition-colors">
            <FileText size={15} /> Prim Ekstrem
          </button>
        </div>
      )}
      {statementOpen && stAccess && <PrimStatementModal yil={stAccess.yil} ay={stAccess.ay} locked onClose={() => setStatementOpen(false)} />}
      {isPersonel && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 md:p-5" data-testid="personel-guide">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-lg bg-blue-600 text-white grid place-items-center">
              <Info size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-base md:text-lg font-bold text-blue-900">Prim Girişi Rehberi</h3>
              <p className="text-xs md:text-sm text-blue-800 mt-1">
                İcra dosyası tahsilatlarını buradan ekleyin — para hesaplamaları ve prim payınız yönetici tarafından ayrıca değerlendirilir.
              </p>
              <ol className="mt-3 space-y-1.5 text-xs md:text-sm text-blue-900 list-decimal list-inside">
                <li><strong>Prim Ekle</strong> butonuna tıklayın.</li>
                <li><strong>Tarih</strong>, <strong>İcra Föy</strong> ve <strong>Esas No</strong> alanlarını girin.</li>
                <li><strong>Dosya Adı</strong> ve <strong>Müvekkil Adı</strong> yazın; biliyorsanız <strong>UYAP</strong> linkini ekleyin.</li>
                <li><strong>Vekalet Ücreti</strong> ve <strong>Tahsilat Miktarı</strong>'nı doldurun — ödenecek rakamı yönetici hesaplar.</li>
                <li>Varsa <strong>Giden Personel</strong>'leri seçin (en fazla 4 kişi).</li>
                <li><strong>Kaydet</strong>'e basın; kayıt listede görünür ve istediğiniz zaman düzenleyebilirsiniz.</li>
              </ol>
              <div className="mt-3 text-[11px] text-blue-700 bg-blue-100/60 border border-blue-200 rounded-lg p-2">
                <strong>Not:</strong> Oran, ödenecek toplam ve araştırma primi bilgileri yalnızca yöneticinin görünümünde yer alır.
              </div>
            </div>
          </div>
        </div>
      )}

      {isAdmin && (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <Info size={16} className="text-amber-700 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-900">
          <strong className="font-semibold">Prim Hesaplama Kuralları:</strong>
          <ul className="mt-1 list-disc list-inside space-y-0.5">
            <li>Müvekkil <strong>Varlık Şirketi</strong> ise: Ödenecek = Tahsilat × Oran (%); kişi başı = Ödenecek ÷ Giden Sayısı</li>
            <li>Diğer müvekkiller için: Ödenecek = Vekalet Ücreti × Oran (%); kişi başı = Ödenecek ÷ Giden Sayısı</li>
            <li><strong>Oto-oran:</strong> Şehir içi/dışı × Varlık matrisine göre %1,5 / %2,5 / %15 / %25 otomatik atanır (manuel değiştirilebilir)</li>
            <li><strong>Araştıran Personel Primi:</strong> Her 1.000.000 ₺ <strong>Tahsilat Miktarı</strong> için 1.500 ₺ (yalnızca Araştıran Kişi atanmış kayıtlarda)</li>
          </ul>
        </div>
      </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 md:p-5 border-b border-slate-200 flex flex-col md:flex-row md:flex-wrap md:items-center md:justify-between gap-3">
          <div>
            <h2 className="font-display text-lg md:text-xl font-bold text-slate-900 flex items-center gap-2">
              <Scale size={18} /> Prim Hesaplama
            </h2>
            <p className="text-xs md:text-sm text-slate-500 mt-0.5">
              {filterAll ? `Tüm kayıtlar · ${filtered.length} prim` : `${AYLAR[ay-1].n} ${yil} · ${filtered.length} prim`}
            </p>
          </div>

          <div className="flex items-center gap-2 md:gap-3 flex-wrap">
            {/* Local year/month selectors — syncs the top-bar via window events */}
            <div className="inline-flex items-center gap-1 bg-slate-100 rounded-lg p-1">
              <select
                data-testid="prim-local-ay"
                value={ay}
                disabled={filterAll}
                onChange={(e) => window.dispatchEvent(new CustomEvent("bordromat:setAy", { detail: Number(e.target.value) }))}
                className="bg-white border border-slate-200 rounded-md px-2 py-1 text-xs font-semibold disabled:opacity-50"
              >
                {AYLAR.map((m) => <option key={m.v} value={m.v}>{m.n}</option>)}
              </select>
              <select
                data-testid="prim-local-yil"
                value={yil}
                disabled={filterAll}
                onChange={(e) => window.dispatchEvent(new CustomEvent("bordromat:setYil", { detail: Number(e.target.value) }))}
                className="bg-white border border-slate-200 rounded-md px-2 py-1 text-xs font-semibold disabled:opacity-50"
              >
                {Array.from({length: 10}, (_, i) => yil - 5 + i).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 w-full md:w-auto">
              <input
                data-testid="prim-filter-all"
                type="checkbox"
                checked={filterAll}
                onChange={(e) => setFilterAll(e.target.checked)}
              />
              Tüm aylar
            </label>
            <ShiftedMonthToggle active={onlyShifted} count={rows.filter(isMonthShifted).length} onToggle={() => setOnlyShifted((v) => !v)} />
            {!isPersonel && (
              <button
                type="button"
                data-testid="prim-filter-cc-only"
                onClick={() => setOnlyCC((v) => !v)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                  onlyCC
                    ? "bg-amber-500 text-white border-amber-600 hover:bg-amber-600"
                    : "bg-white text-amber-700 border-amber-300 hover:bg-amber-50"
                }`}
                title="Yalnızca Call Center personeli içeren prim satırları"
              >
                <span className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-[8px] font-bold leading-none ${onlyCC ? "bg-white text-amber-700" : "bg-amber-500 text-white"}`}>%</span>
                Yalnızca CC
                {onlyCC && <X size={12} />}
              </button>
            )}
            {!isPersonel && (
              <select
                data-testid="prim-personel-filter"
                value={personelFilter}
                onChange={(e) => setPersonelFilter(e.target.value)}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm font-medium bg-white max-w-[200px] focus:outline-none focus:ring-2 focus:ring-slate-900"
                title="Belirli bir personele ait prim kayıtlarını filtrele"
              >
                <option value="">Tüm Personel</option>
                {personnel.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.ad_soyad} {p.sicil_no ? `· ${p.sicil_no}` : ""}
                  </option>
                ))}
              </select>
            )}
            <div className="relative flex-1 md:flex-none">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="prim-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Dosya, müvekkil, föy ara..."
                className="pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm w-full md:w-64 focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
            </div>
            <div className="grid grid-cols-2 md:flex md:items-center md:gap-3 gap-2 w-full md:w-auto">
              {isAdmin && (
                <>
                  <button
                    data-testid="export-prim-button"
                    onClick={() => setShowExportModal(true)}
                    className="flex items-center justify-center gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-2 rounded-lg text-sm font-semibold"
                  >
                    <Download size={14} /> Dışa Aktar
                  </button>
                  <button
                    data-testid="export-prim-summary-button"
                    onClick={async () => {
                      try {
                        const token = getToken();
                        const qs = filterAll ? "" : `?yil=${yil}&ay=${ay}`;
                        const res = await fetch(`${API}/prim/summary/export${qs}`, { headers: { Authorization: `Bearer ${token}` } });
                        if (!res.ok) throw new Error();
                        const blob = await res.blob();
                        const u = URL.createObjectURL(blob);
                        const a = document.createElement("a"); a.href = u;
                        a.download = `prim_ozeti_${filterAll ? "tumu" : yil + "_" + String(ay).padStart(2, "0")}.xlsx`;
                        document.body.appendChild(a); a.click(); a.remove();
                        URL.revokeObjectURL(u);
                        toast.success("Personel bazlı aylık özet indirildi");
                      } catch { toast.error("Özet indirilemedi"); }
                    }}
                    className="flex items-center justify-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg text-sm font-semibold"
                    title="Personel bazlı aylık prim özetini Excel olarak indir"
                  >
                    <Download size={14} /> Aylık Özet
                  </button>
                  <button
                    data-testid="all-pdf-button"
                    onClick={handleAllPdf}
                    className="flex items-center justify-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white px-3 py-2 rounded-lg text-sm font-semibold"
                    title="Seçili ayın tüm prim makbuzlarını tek PDF'te indir"
                  >
                    <FilePlus size={14} /> Tüm PDF
                  </button>
                  <button
                    data-testid="import-prim-button"
                    onClick={() => setImportOpen(true)}
                    className="flex items-center justify-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white px-3 py-2 rounded-lg text-sm font-semibold"
                  >
                    <Upload size={14} /> İçe Aktar
                  </button>
                  {isAdmin && (
                    <button
                      data-testid="open-trash-button"
                      onClick={() => setTrashOpen(true)}
                      className="flex items-center justify-center gap-1.5 bg-slate-500 hover:bg-slate-600 text-white px-3 py-2 rounded-lg text-sm font-semibold"
                      title="Son 24 saatte silinen primler · geri yükle"
                    >
                      <Trash2 size={14} /> Çöp Kutusu
                    </button>
                  )}
                  {(isAdmin || isMuhasebeci) && (
                    <button
                      data-testid="open-varlik-audit-button"
                      onClick={() => setVarlikAuditOpen(true)}
                      className="flex items-center justify-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 rounded-lg text-sm font-semibold"
                      title='Müvekkil adı "varlık yönetim" içeren ama Varlık Şirketi = Hayır olarak işaretlenmiş primler'
                    >
                      <ShieldAlert size={14} /> Varlık Denetimi
                    </button>
                  )}
                </>
              )}
              <button
                data-testid="add-prim-button"
                onClick={() => { setEditing(null); setModalOpen(true); }}
                className="flex items-center justify-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-3 md:px-4 py-2 rounded-lg text-sm font-semibold col-span-2 md:col-span-1"
              >
                <Plus size={16} /> Prim Ekle
              </button>
            </div>
          </div>
        </div>

        {/* Approval status chip filter — Hepsi / Onaylanan / Onay Bekleyen */}
        <div className="px-4 md:px-5 py-2.5 border-b border-slate-200 bg-slate-50/60" data-testid="prim-approval-filter-bar">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600 shrink-0">Onay durumu:</span>
            <button
              type="button"
              data-testid="prim-approval-filter-all"
              onClick={() => setApprovalFilter(null)}
              className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border transition-colors ${
                approvalFilter === null
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >
              Hepsi · {rows.length}
            </button>
            <button
              type="button"
              data-testid="prim-approval-filter-approved"
              onClick={() => setApprovalFilter(approvalFilter === true ? null : true)}
              className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border inline-flex items-center gap-1 transition-colors ${
                approvalFilter === true
                  ? "bg-emerald-600 text-white border-emerald-600"
                  : "bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50"
              }`}
            >
              Onaylanan
              <span className={`text-[9px] font-bold ${approvalFilter === true ? "text-emerald-100" : "text-slate-400"}`}>{approvedCount}</span>
            </button>
            <button
              type="button"
              data-testid="prim-approval-filter-pending"
              onClick={() => setApprovalFilter(approvalFilter === false ? null : false)}
              className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border inline-flex items-center gap-1 transition-colors ${
                approvalFilter === false
                  ? "bg-amber-500 text-white border-amber-500"
                  : "bg-white text-amber-700 border-amber-200 hover:bg-amber-50"
              }`}
            >
              Onay Bekleyen
              <span className={`text-[9px] font-bold ${approvalFilter === false ? "text-amber-100" : "text-slate-400"}`}>{pendingCount}</span>
            </button>
          </div>
        </div>

        {/* Tag chip filter bar — visible when at least one row has a tag */}
        {tagChipCounts.length > 0 && (
          <div className="px-4 md:px-5 py-2.5 border-b border-slate-200 bg-sky-50/60" data-testid="prim-tag-filter-bar">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wider text-sky-700 shrink-0">Etikete göre filtrele:</span>
              <button
                type="button"
                data-testid="prim-tag-filter-all"
                onClick={() => setTagFilter([])}
                className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border transition-colors ${
                  tagFilter.length === 0
                    ? "bg-sky-700 text-white border-sky-700"
                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
              >
                Tümü · {rows.length}
              </button>
              {tagChipCounts.map((tc) => {
                const active = tagFilter.some((t) => t.toLocaleLowerCase("tr") === tc.label.toLocaleLowerCase("tr"));
                return (
                  <button
                    key={tc.label}
                    type="button"
                    data-testid={`prim-tag-filter-${tc.label}`}
                    onClick={() => {
                      setTagFilter((prev) => {
                        const lower = tc.label.toLocaleLowerCase("tr");
                        const idx = prev.findIndex((t) => t.toLocaleLowerCase("tr") === lower);
                        if (idx >= 0) {
                          return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
                        }
                        return [...prev, tc.label];
                      });
                    }}
                    className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border inline-flex items-center gap-1 transition-colors ${
                      active
                        ? "bg-sky-600 text-white border-sky-600"
                        : "bg-white text-sky-700 border-sky-200 hover:bg-sky-100"
                    }`}
                    title={`#${tc.label} · ${tc.count} prim`}
                  >
                    # {tc.label}
                    <span className={`text-[9px] font-bold ${active ? "text-sky-100" : "text-slate-400"}`}>{tc.count}</span>
                  </button>
                );
              })}
              {tagFilter.length >= 2 && (
                <div className="inline-flex rounded-md overflow-hidden border border-slate-300 text-[10px] font-bold" data-testid="prim-tag-filter-mode">
                  <button
                    type="button"
                    onClick={() => setTagFilterMode("or")}
                    data-testid="prim-tag-filter-mode-or"
                    className={`px-2 py-0.5 ${tagFilterMode === "or" ? "bg-indigo-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                    title="Herhangi biri (OR): seçili etiketlerden en az biri olan satırlar"
                  >
                    HERHANGİ
                  </button>
                  <button
                    type="button"
                    onClick={() => setTagFilterMode("and")}
                    data-testid="prim-tag-filter-mode-and"
                    className={`px-2 py-0.5 ${tagFilterMode === "and" ? "bg-indigo-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                    title="Tümü (AND): seçili etiketlerin hepsini içeren satırlar"
                  >
                    TÜMÜ
                  </button>
                </div>
              )}
              {isAdmin && (
                <button
                  type="button"
                  data-testid="prim-tag-manager-btn"
                  onClick={() => setTagMgrOpen(true)}
                  className="ml-auto text-[11px] font-semibold px-3 py-0.5 rounded-full border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                  title="Tüm etiketleri yönet (yeniden adlandır, birleştir, sil)"
                >
                  Etiket Yöneticisi
                </button>
              )}
            </div>
          </div>
        )}

        {/* Advanced universal column filters (admin / muhasebeci only) */}
        {!isPersonel && (
          <div className="px-4 md:px-5 py-3 border-b border-slate-200 bg-slate-50/70">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setFiltersOpen((x) => !x)}
                data-testid="prim-filter-toggle"
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md ${
                  filtersOpen || countPrimColFilters(colFilters) > 0
                    ? "bg-indigo-600 text-white"
                    : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
                }`}
              >
                <Filter size={13} /> Gelişmiş Filtreler
                {countPrimColFilters(colFilters) > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white text-indigo-700 text-[10px] font-bold">
                    {countPrimColFilters(colFilters)}
                  </span>
                )}
              </button>
              {countPrimColFilters(colFilters) > 0 && (
                <button
                  type="button"
                  onClick={() => { setColFilters({}); setTimeout(load, 50); }}
                  className="text-xs text-slate-500 hover:text-slate-900 font-semibold"
                  data-testid="prim-filters-clear"
                >
                  Temizle
                </button>
              )}
              <div className="ml-auto">
                <SavedFilterPresets
                  scope="prim"
                  currentFilters={{}}
                  currentColFilters={colFilters}
                  onApply={(_, cf) => { setColFilters(cf || {}); setFiltersOpen(true); setTimeout(load, 50); }}
                />
              </div>
            </div>

            {filtersOpen && (
              <div className="mt-3 bg-white border border-slate-200 rounded-lg p-3 space-y-3" data-testid="prim-filters-panel">
                <QuickDateFilters
                  label="Tarih Aralığı"
                  testPrefix="quick-prim"
                  onApply={({ from, to }) => {
                    setColFilters((prev) => ({ ...prev, tarih: { min: from, max: to } }));
                    setTimeout(load, 50);
                  }}
                />
                <div className="flex items-center gap-2 border-t border-slate-100 pt-3">
                  <Columns3 size={13} className="text-slate-500" />
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Sütun Filtreleri</div>
                  <select
                    value={addColKey}
                    onChange={(e) => {
                      const k = e.target.value;
                      if (!k) return;
                      setColFilters((prev) => ({ ...prev, [k]: prev[k] || (PRIM_COLUMN_META_BY_KEY[k].t === "text" ? { value: "" } : { min: "", max: "" }) }));
                      setAddColKey("");
                    }}
                    className="ml-auto text-xs px-2 py-1 border border-slate-300 rounded bg-white"
                    data-testid="prim-filter-add-column"
                  >
                    <option value="">+ Sütun Ekle</option>
                    {PRIM_COLUMN_META.filter((c) => !colFilters[c.k]).map((c) => (
                      <option key={c.k} value={c.k}>{c.l}</option>
                    ))}
                  </select>
                </div>
                {Object.keys(colFilters).length === 0 && (
                  <div className="text-[11px] text-slate-400 italic">Henüz sütun filtresi eklenmedi. Metin arama, sayı veya tarih aralığı için sütun ekleyin.</div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries(colFilters).map(([k, cond]) => {
                    const meta = PRIM_COLUMN_META_BY_KEY[k];
                    if (!meta) return null;
                    const update = (patch) => setColFilters((prev) => ({ ...prev, [k]: { ...prev[k], ...patch } }));
                    const remove = () => setColFilters((prev) => { const { [k]: _, ...rest } = prev; return rest; });
                    return (
                      <div key={k} className="bg-slate-50 border border-slate-200 rounded-md p-2 flex items-center gap-2" data-testid={`prim-colfilter-row-${k}`}>
                        <div className="text-[11px] font-bold text-slate-700 min-w-[120px] truncate" title={meta.l}>{meta.l}</div>
                        {meta.t === "text" && (
                          <input
                            type="text"
                            value={cond.value || ""}
                            onChange={(e) => update({ value: e.target.value })}
                            placeholder="içeren..."
                            className="flex-1 px-2 py-1 border border-slate-300 rounded text-xs"
                            data-testid={`prim-colfilter-value-${k}`}
                          />
                        )}
                        {meta.t === "number" && (
                          <div className="flex-1 flex items-center gap-1">
                            <input type="number" step="0.01" value={cond.min || ""} onChange={(e) => update({ min: e.target.value })} placeholder="min" className="w-full px-2 py-1 border border-slate-300 rounded text-xs" data-testid={`prim-colfilter-min-${k}`} />
                            <span className="text-slate-400 text-xs">—</span>
                            <input type="number" step="0.01" value={cond.max || ""} onChange={(e) => update({ max: e.target.value })} placeholder="max" className="w-full px-2 py-1 border border-slate-300 rounded text-xs" data-testid={`prim-colfilter-max-${k}`} />
                          </div>
                        )}
                        {meta.t === "date" && (
                          <div className="flex-1 flex items-center gap-1">
                            <DateInput value={cond.min || ""} onChange={(e) => update({ min: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-xs" data-testid={`prim-colfilter-min-${k}`} />
                            <span className="text-slate-400 text-xs">—</span>
                            <DateInput value={cond.max || ""} onChange={(e) => update({ max: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-xs" data-testid={`prim-colfilter-max-${k}`} />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={remove}
                          className="shrink-0 w-6 h-6 rounded hover:bg-rose-100 text-rose-600 grid place-items-center"
                          data-testid={`prim-colfilter-remove-${k}`}
                          title="Kaldır"
                        >
                          <X size={13} />
                        </button>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-end">
                  <button onClick={load} data-testid="prim-filter-apply-btn" className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-md">
                    Uygula
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Selected personnel breakdown banner */}
        {personelDetay && (
          <div
            data-testid="prim-personel-detay"
            className="px-4 md:px-5 py-3 bg-gradient-to-r from-emerald-50 to-indigo-50 border-b border-slate-200 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm"
          >
            <span className="font-semibold text-slate-900 flex items-center gap-2">
              <Scale size={14} className="text-slate-500" />
              {personnel.find((p) => p.id === personelFilter)?.ad_soyad || "—"}
              <button
                data-testid="prim-personel-filter-clear"
                onClick={() => setPersonelFilter("")}
                className="ml-1 text-slate-400 hover:text-slate-700"
                title="Filtreyi temizle"
              >
                <X size={14} />
              </button>
            </span>
            <span className="text-slate-600">
              Giden: <strong className="text-emerald-700 font-mono-tab" data-testid="prim-detay-giden">{personelDetay.giden_adet}</strong> dosya ·{" "}
              <strong className="text-emerald-700 font-mono-tab">{formatTL(personelDetay.giden_prim)}</strong>
            </span>
            <span className="text-slate-600">
              Araştıran: <strong className="text-indigo-700 font-mono-tab" data-testid="prim-detay-arastiran">{personelDetay.arastiran_adet}</strong> dosya ·{" "}
              <strong className="text-indigo-700 font-mono-tab">{formatTL(personelDetay.arastiran_prim)}</strong>
            </span>
            <span className="ml-auto text-slate-800">
              Toplam Prim: <strong className="text-slate-900 font-mono-tab text-base" data-testid="prim-detay-toplam">{formatTL(personelDetay.giden_prim + personelDetay.arastiran_prim)}</strong>
            </span>
          </div>
        )}

        {/* Bulk Varlık + Delete toolbar (admin/muhasebeci when ≥1 row selected) */}
        {(isAdmin || isMuhasebeci) && selectedIds.size > 0 && (
          <div
            data-testid="prim-bulk-toolbar"
            className="flex flex-wrap items-center justify-between gap-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 text-xs text-indigo-900"
          >
            <div>
              <strong>{selectedIds.size}</strong> prim seçildi
            </div>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <>
                  <button
                    data-testid="bulk-varlik-yes"
                    onClick={() => bulkSetVarlik(true)}
                    disabled={bulkBusy}
                    className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-md font-semibold disabled:opacity-50"
                  >Varlık = EVET</button>
                  <button
                    data-testid="bulk-varlik-no"
                    onClick={() => bulkSetVarlik(false)}
                    disabled={bulkBusy}
                    className="inline-flex items-center gap-1.5 bg-slate-700 hover:bg-slate-800 text-white px-3 py-1.5 rounded-md font-semibold disabled:opacity-50"
                  >Varlık = HAYIR</button>
                </>
              )}
              <button
                data-testid="prim-bulk-delete"
                onClick={bulkDelete}
                disabled={bulkBusy}
                className="inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 rounded-md font-semibold disabled:opacity-50"
                title="Seçili primleri kalıcı olarak sil"
              >
                <Trash2 size={13} /> Sil ({selectedIds.size})
              </button>
              <button
                data-testid="bulk-varlik-clear"
                onClick={clearRowSelection}
                className="inline-flex items-center gap-1 text-indigo-700 hover:underline font-semibold px-2 py-1.5"
              >Temizle</button>
            </div>
          </div>
        )}

        {/* Mobile cards (<768px) */}
        <PrimMobileCards
          rows={filtered}
          totals={totals}
          loading={loading}
          canSeePrimRate={canSeePrimRate}
          isPersonel={isPersonel}
          onEdit={(r) => { setEditing(r); setModalOpen(true); }}
          onDelete={handleDelete}
          onPdf={handlePdf}
        />

        <PrimTable
          rows={filtered}
          totals={totals}
          loading={loading}
          canSeePrimRate={canSeePrimRate}
          isPersonel={isPersonel}
          isAdmin={isAdmin}
          onEdit={(r) => { setEditing(r); setModalOpen(true); }}
          onDelete={handleDelete}
          onPdf={handlePdf}
          onApprove={handleApprove}
          onReject={handleReject}
          onToggleVarlik={handleToggleVarlik}
          onSetVisibility={handleSetVisibility}
          selectedIds={selectedIds}
          onToggleRow={toggleRowSelect}
        />
      </div>

      {modalOpen && (
        <PrimModal
          editing={editing}
          personnel={personnel}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); load(); }}
        />
      )}

      {importOpen && (
        <ImportModal
          onClose={() => setImportOpen(false)}
          onDone={() => { setImportOpen(false); load(); }}
          onDownloadTemplate={handleDownloadTemplate}
        />
      )}

      {signaturePrim && (
        <SignatureModal
          existingImza={signaturePrim.imza_base64 || ""}
          loading={sigLoading}
          onCancel={() => setSignaturePrim(null)}
          onSubmit={handleSignatureSubmit}
        />
      )}

      {showExportModal && (
        <PrimExportModal
          yil={yil}
          ay={ay}
          filterAll={filterAll}
          cfString={serializePrimColFilters(colFilters)}
          cfCount={countPrimColFilters(colFilters)}
          onClose={() => setShowExportModal(false)}
        />
      )}

      {trashOpen && (
        <PrimTrashModal
          onClose={() => setTrashOpen(false)}
          onRestored={() => load()}
        />
      )}

      {varlikAuditOpen && (
        <VarlikAuditModal
          onClose={() => setVarlikAuditOpen(false)}
          onFixed={() => load()}
        />
      )}

      {tagMgrOpen && (
        <TagManagerModal
          onClose={() => setTagMgrOpen(false)}
          onChanged={() => load()}
        />
      )}
    </div>
  );
}

// =========================
// PersonelInboxBanner — admin reddet mesajları için personel'e bildirim
// =========================
function PersonelInboxBanner({ messages, onDismiss, onDismissAll }) {
  const unread = messages.filter((m) => !m.read_at);
  if (unread.length === 0 && messages.length > 0) {
    // show nothing when there are only read messages
    return null;
  }
  const items = unread.length > 0 ? unread : [];
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  };
  return (
    <div
      data-testid="personel-inbox-banner"
      className="bg-rose-50 border border-rose-200 rounded-xl p-3 shadow-sm"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <div className="relative w-7 h-7 rounded-md bg-rose-500 text-white grid place-items-center">
            <Bell size={14} />
            <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] text-[10px] font-bold bg-white text-rose-600 rounded-full border border-rose-200 inline-flex items-center justify-center px-1">
              {items.length}
            </span>
          </div>
          <div>
            <div className="font-display text-sm font-extrabold text-rose-900 leading-tight">
              Yönetici Mesajları
            </div>
            <div className="text-[11px] text-rose-700 font-semibold uppercase tracking-wider">
              {items.length} okunmamış · reddedilen primlerle ilgili geri bildirim
            </div>
          </div>
        </div>
        <button
          data-testid="personel-inbox-dismiss-all"
          onClick={onDismissAll}
          className="inline-flex items-center gap-1 bg-white border border-rose-200 text-rose-700 hover:bg-rose-100 rounded-md px-2 py-1 text-[11px] font-semibold"
        >
          <MailOpen size={11} /> Hepsini Okundu
        </button>
      </div>
      <div className="space-y-1.5">
        {items.slice(0, 5).map((m) => (
          <div
            key={m.id}
            data-testid={`personel-inbox-msg-${m.id}`}
            className="bg-white border border-rose-200 rounded-md px-2.5 py-1.5 flex items-start gap-2 text-xs"
          >
            <X size={14} className="text-rose-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-slate-800 truncate" title={m.prim_label}>
                {m.prim_label || "Prim"}
              </div>
              <div className="text-slate-700 whitespace-pre-wrap leading-snug">
                "{m.message}"
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1.5">
                <span>{m.from_admin_email || "yönetici"}</span>
                <span>·</span>
                <span>{fmt(m.created_at)}</span>
              </div>
            </div>
            <button
              data-testid={`personel-inbox-dismiss-${m.id}`}
              onClick={() => onDismiss(m.id)}
              className="text-slate-400 hover:text-slate-600 shrink-0"
              title="Okundu olarak işaretle"
            >
              <X size={13} />
            </button>
          </div>
        ))}
        {items.length > 5 && (
          <div className="text-[10px] text-rose-700 font-semibold text-center">
            +{items.length - 5} daha · "Hepsini Okundu" ile temizleyebilirsiniz
          </div>
        )}
      </div>
    </div>
  );
}
